// src/contexts/ChatbotContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';

import { CircularProgress, Box, Typography } from '@mui/material';

const ChatbotContext = createContext();

export const useChatbots = () => {
  return useContext(ChatbotContext);
};

export const ChatbotProvider = ({ children }) => {
  const [chatbots, setChatbots] = useState([]);
  const [activeChatbot, setActiveChatbot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const navigate = useNavigate();

  // Chatbot API not available in trading app — skip fetch, provide empty defaults
  const fetchChatbots = useCallback(async (callback) => {
    setIsLoading(false);
    setHasInitialized(true);
    if (callback) callback([]);
  }, []);

  useEffect(() => {
    if (!hasInitialized) {
      setIsLoading(false);
      setHasInitialized(true);
    }
  }, [hasInitialized]);

  const initializeActiveBot = useCallback((bots) => {
    console.log('Initializing active bot from:', bots.length, 'bots');

    let userData = {};
    const userStr = localStorage.getItem('user');
    if (userStr) {
        userData = JSON.parse(userStr);
    }

    if (!('activeChatbotId' in userData.active_team)){
        userData = {
        ...userData,
        active_team: {
        ...userData.active_team,
        activeChatbotId: null,
        },
        teams: userData.teams.map(team =>
        team.team_id === userData.active_team.team_id
            ? { ...team, activeChatbotId: null}
            : team
        ),
      };
    }
    let activeBotInList = null
    if (userData.active_team.activeChatbotId) {
      // Try to find by ID first, then by chatbot_identity
      activeBotInList = bots.find(b => 
        b.id.toString() === userData.active_team.activeChatbotId || 
        b.chatbot_identity === userData.active_team.activeChatbotId
      );
    }

    userData = {
      ...userData,
      active_team: {
      ...userData.active_team,
      chatbots: bots,
      },
      teams: userData.teams.map(team =>
      team.team_id === userData.active_team.team_id
          ? { ...team, chatbots: bots}
          : team
      ),
    };
    
    if (activeBotInList) {
      console.log('Found stored active bot:', activeBotInList.chatbot_name);
      setActiveChatbot(activeBotInList);
    } else if (bots.length > 0) {
      const defaultBot = bots.find(b => b.is_default) || bots[0];
      console.log('Setting default bot:', defaultBot.chatbot_name);
      setActiveChatbot(defaultBot);
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      console.log('No bots available');
      setActiveChatbot(null);
    }
  }, []);

  const switchActiveChatbot = useCallback((chatbotId, options = {}) => {
    const { navigateToDashboard = false } = options;
    
    console.log('Switching to chatbot:', chatbotId, 'navigate:', navigateToDashboard);
    
    // Find by ID or chatbot_identity
    const newActiveBot = chatbots.find(b => 
      b.id.toString() === chatbotId.toString() || 
      b.chatbot_identity === chatbotId.toString()
    );

    let userData = {};
    const userStr = localStorage.getItem('user');
    if (userStr) {
        userData = JSON.parse(userStr);
    }

    if (!('activeChatbotId' in userData.active_team)){
        userData = {
        ...userData,
        active_team: {
        ...userData.active_team,
        activeChatbotId: null,
        },
        teams: userData.teams.map(team =>
        team.team_id === userData.active_team.team_id
            ? { ...team, activeChatbotId: null}
            : team
        ),
      };
    }
    
    if (newActiveBot) {
      console.log('Found bot to switch to:', newActiveBot.chatbot_name);
      setActiveChatbot(newActiveBot);

      userData = {
        ...userData,
        active_team: {
        ...userData.active_team,
        activeChatbotId: newActiveBot.id,
        },
        teams: userData.teams.map(team =>
        team.team_id === userData.active_team.team_id
            ? { ...team, activeChatbotId: newActiveBot.id}
            : team
        ),
      };
      localStorage.setItem('user', JSON.stringify(userData));

      // Only navigate if explicitly requested
      if (navigateToDashboard) {
        console.log('Navigating to dashboard for:', newActiveBot.chatbot_identity);
        navigate(`/chatbot/${newActiveBot.chatbot_identity}/dashboard`);
      }
    } else {
      console.warn('Could not find chatbot with ID:', chatbotId);
    }
  }, [chatbots, navigate]);

  // Properly memoize refreshChatbots with fetchChatbots as dependency
  const refreshChatbots = useCallback((callback) => {
    console.log('Refreshing chatbots...');
    fetchChatbots(callback);
  }, [fetchChatbots]);

  const value = {
    chatbots,
    activeChatbot,
    isLoading,
    switchActiveChatbot,
    refreshChatbots,
  };

  // Only show global loading on initial load
  if (isLoading && chatbots.length === 0 && !hasInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Application...</Typography>
      </Box>
    );
  }

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  );
};